import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptProposal,
  acceptSurfacedProposal,
  dismissProposal,
  fetchOriginalRules,
  fetchRulesVersion,
  fetchVersions,
  proposeRules,
  rollbackRules,
  type HouseRules,
  type Proposal,
  type RulesFailure,
  type RulesVersionSummary,
  type SurfacedProposal,
} from "../../lib/api";
import { sectionsChanged } from "../../lib/rules";
import { currentRulesQuery, proposalsQuery, qk } from "../../lib/queries";
import { track } from "../../lib/telemetry";
import { RulesHome } from "./home";
import { ProposalsSection } from "./proposals";
import { RulesEditor } from "./editor";
import { DiffView } from "./diff-view";
import { RulesHistory, RulesVersionView } from "./history";

/** How long an acknowledgement stays on the home screen before it gets out of the way. */
const NOTE_MS = 2600;

/** The document as one comparable value — version alone is not enough mid-edit. */
const stampOf = (rules: { version: number; markdown: string }): string =>
  `${rules.version} ${rules.markdown}`;

type View = "home" | "manual" | "diff" | "history" | "version";

type Props = {
  /** Rendered immediately so the document is never blank while it refreshes. */
  initial: HouseRules;
  handle: string | null;
  /** Lets the shell keep its saved copy of the door current (FR-2.5). */
  onRulesChanged: (rules: HouseRules) => void;
  onSessionExpired: () => void;
  onBack?: (() => void) | undefined;
  /** What the back link is called — "Your door" in onboarding, "Morning Nob" from home (M16). */
  backLabel?: string | undefined;
  /**
   * "Adjust" on a card in the queue (ruling R-0915-1 (4)): the panel closes and
   * the owner's own conversation opens with the composer pointed at that card.
   * It used to open the editor here on the proposed document; the ruling
   * reverses that, because the owner is not meant to write `nob.md` by hand.
   */
  onAdjustProposal: (proposalId: string) => void;
  /** To the doorman chat at Home — where telling Nob happens. */
  onTellNob: () => void;
};

type Message = { text: string; line: string | null };

/** Nob's side of every failure, in Nob's words (PRD §5.0 codes). */
export function messageFor(failure: RulesFailure): Message {
  switch (failure.code) {
    case "RULES_TOO_LARGE":
      return { text: "Your rules are as long as Nob will hold.", line: null };
    case "INPUT_REFUSED":
      return { text: "Nob won’t write that into its own instructions.", line: failure.line };
    case "ALREADY_RESOLVED":
      return { text: "That change has already been settled. Start it again.", line: null };
    case "PROPOSAL_NOT_FOUND":
      return { text: "That change is no longer on the table. Start it again.", line: null };
    case "RULES_VERSION_NOT_FOUND":
      return { text: "That version is not there to restore.", line: null };
    case "DOOR_NOT_FOUND":
      return { text: "Nob has lost track of your door. Reload and try again.", line: null };
    case "REGULATED_CATEGORY":
      return {
        text: "That is not available in your jurisdiction. Take it out and try again.",
        line: null,
      };
    case "VALIDATION_ERROR":
      return { text: "Nob could not read that. Put it another way.", line: null };
    case "UNAUTHENTICATED":
    case "UNKNOWN":
      return { text: "Nob is not answering. Try that again.", line: null };
  }
}

/**
 * The House Rules editor (M5, PRD FR-2.5 / FR-8.5). Three ways in — type it,
 * say it, or restore it — and one way through: a diff nobody has saved yet.
 *
 * Network state lives here; every screen below is a pure render of what it is
 * given. A flagged contradiction is a note on the review screen and nothing
 * more (ruling R-0905-6): there is no resolution state to hold.
 */
export function RulesArea({
  initial,
  handle,
  onRulesChanged,
  onSessionExpired,
  onBack,
  backLabel,
  onTellNob,
  onAdjustProposal,
}: Props) {
  const queryClient = useQueryClient();
  const [rules, setRules] = useState<HouseRules>(initial);
  const [view, setView] = useState<View>("home");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [draft, setDraft] = useState(initial.markdown);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [source, setSource] = useState<"manual" | "tell">("manual");

  const [versions, setVersions] = useState<RulesVersionSummary[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [viewing, setViewing] = useState<HouseRules | null>(null);

  // Surfaced proposals (M15, FR-8.3). The list, and the transient
  // busy/dismissed marks each card is a pure render of.
  const [proposals, setProposals] = useState<SurfacedProposal[]>([]);
  const [propBusy, setPropBusy] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fadeTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = fadeTimers.current;
    return () => {
      clearTimeout(noteTimer.current);
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  // The seed and the shell's callback are held in refs so the refresh below is
  // a mount-time effect: re-seeding the component must never refetch.
  const seed = useRef(initial);
  const notify = useRef(onRulesChanged);
  notify.current = onRulesChanged;

  const say = useCallback((text: string) => {
    setNote(text);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(null), NOTE_MS);
  }, []);

  /** Every failure route ends here: expired sessions leave, the rest are read out. */
  const fail = useCallback(
    (failure: RulesFailure): void => {
      if (failure.code === "UNAUTHENTICATED") {
        onSessionExpired();
        return;
      }
      setMessage(messageFor(failure));
    },
    [onSessionExpired],
  );

  // The saved copy is only ever a cache; the server holds the current version.
  const { data: current } = useQuery(currentRulesQuery());
  /** What has already been taken from the cache — a re-read of the same document
   * is not a second viewing, and must not be reported as one. */
  const applied = useRef<string | null>(null);
  useEffect(() => {
    if (!current) return;
    const stamp = stampOf(current);
    if (applied.current === stamp) return;
    applied.current = stamp;
    const next = { version: current.version, markdown: current.markdown };
    setRules(next);
    setDraft(next.markdown);
    track({ name: "rules_viewed", version: next.version, chars: next.markdown.length });
    if (next.version !== seed.current.version || next.markdown !== seed.current.markdown) {
      notify.current(next);
    }
  }, [current]);

  // The proposals waiting on this door (FR-8.3). They are an offer, never
  // urgent, so a read that fails simply surfaces nothing. The list is held here
  // once it lands, because a card carries a fade the server knows nothing about.
  const { data: surfaced } = useQuery(proposalsQuery());
  /** Surfacing is a per-proposal event, so it is reported per proposal — once. */
  const announced = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!surfaced) return;
    setProposals(surfaced);
    for (const p of surfaced) {
      if (announced.current.has(p.id)) continue;
      announced.current.add(p.id);
      track({
        name: "rule_proposal_surfaced",
        trigger_kind: p.trigger_kind,
        signal_count: p.signal_count,
        had_conflict: p.conflicts.length > 0,
      });
    }
  }, [surfaced]);

  const propose = useCallback(
    async (input: { instruction: string } | { markdown: string }, from: "manual" | "tell") => {
      setBusy(true);
      setMessage(null);
      const r = await proposeRules(input);
      setBusy(false);
      if (!r.ok) {
        fail(r);
        return;
      }
      setProposal(r.proposal);
      setSource(from);
      setView("diff");
    },
    [fail],
  );

  /** Settle every card that carries this id: it is off the queue for good. */
  const removeProposal = useCallback(
    (id: string) => {
      const timer = fadeTimers.current.get(id);
      if (timer) clearTimeout(timer);
      fadeTimers.current.delete(id);
      setProposals((prev) => prev.filter((p) => p.id !== id));
      // The badge on the way in here counts the same queue, so it goes too.
      queryClient.setQueryData<SurfacedProposal[]>(qk.proposals(), (prev) =>
        prev?.filter((p) => p.id !== id),
      );
    },
    [queryClient],
  );

  const accept = useCallback(async () => {
    if (!proposal) return;
    setBusy(true);
    setMessage(null);

    /**
     * ONE SAVE PATH, because there is one way into this editor (ruling
     * R-0915-1 (4)). A second one existed while "Edit" on a queued proposal
     * opened the box on the PROPOSED document and settled that proposal by id
     * on the way out. That verb is Adjust now and it never comes here — the
     * owner says what to change in the conversation instead — so what remains
     * is the editor's own document, saved as a version of its own.
     */
    const r = await acceptProposal(proposal.proposal_id);
    setBusy(false);
    if (!r.ok) {
      fail(r);
      return;
    }
    track({
      name: "rules_edited",
      version: r.rules.version,
      author: source === "manual" ? "recipient" : "nob_proposal",
      chars: r.rules.markdown.length,
      sections_changed: sectionsChanged(rules.markdown, r.rules.markdown),
    });
    applied.current = stampOf(r.rules);
    setRules(r.rules);
    setDraft(r.rules.markdown);
    setProposal(null);
    setVersions([]);
    onRulesChanged(r.rules);
    say(`Saved as version ${r.rules.version}.`);
    setView("home");
  }, [proposal, source, rules.markdown, onRulesChanged, removeProposal, fail, say]);

  const openHistory = useCallback(async () => {
    setMessage(null);
    setView("history");
    setLoadingVersions(true);
    const r = await fetchVersions();
    setLoadingVersions(false);
    if (!r.ok) {
      fail(r);
      return;
    }
    setVersions(r.versions);
  }, [fail]);

  const openVersion = useCallback(
    async (version: number) => {
      setMessage(null);
      setBusy(true);
      const r = await fetchRulesVersion(version);
      setBusy(false);
      if (!r.ok) {
        fail(r);
        return;
      }
      setViewing(r.rules);
      setView("version");
    },
    [fail],
  );

  const restore = useCallback(async () => {
    if (!viewing) return;
    const from = viewing.version;
    setBusy(true);
    setMessage(null);
    const r = await rollbackRules(from);
    setBusy(false);
    if (!r.ok) {
      fail(r);
      return;
    }
    track({
      name: "rules_rolled_back",
      version: r.rules.version,
      author: "rollback",
      chars: r.rules.markdown.length,
      sections_changed: sectionsChanged(rules.markdown, r.rules.markdown),
      from_version: from,
    });
    applied.current = stampOf(r.rules);
    setRules(r.rules);
    setDraft(r.rules.markdown);
    setViewing(null);
    setVersions([]);
    onRulesChanged(r.rules);
    say(`Version ${r.rules.version}. Restored from version ${from}.`);
    setView("home");
  }, [viewing, rules.markdown, onRulesChanged, fail, say]);

  /**
   * RESTORE DEFAULT (ruling R-0910-4, amended). It reads the document the
   * wizard composed and submits it as a proposal — the same call a hand edit
   * makes, so the diff renders, the warnings render, and Accept is what writes
   * the version. The button itself saves nothing, which is the whole rule this
   * editor is built on. Where the original is already the current document,
   * the proposal comes back with no changes and the diff says so.
   */
  const restoreDefault = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    const r = await fetchOriginalRules();
    setBusy(false);
    if (!r.ok) {
      fail(r);
      return;
    }
    setDraft(r.markdown);
    await propose({ markdown: r.markdown }, "manual");
  }, [fail, propose]);

  const discard = useCallback(() => {
    setProposal(null);
    setMessage(null);
    setDraft(rules.markdown);
    say("Nothing was saved.");
    setView("home");
  }, [rules.markdown, say]);

  const home = useCallback(() => {
    setMessage(null);
    setView("home");
  }, []);

  // --- surfaced proposals (M15, FR-8.3) -------------------------------------

  const addProposal = useCallback(
    async (p: SurfacedProposal) => {
      setPropBusy((prev) => ({ ...prev, [p.id]: true }));
      setMessage(null);
      const r = await acceptSurfacedProposal(p.id);
      setPropBusy((prev) => ({ ...prev, [p.id]: false }));
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") {
          onSessionExpired();
          return;
        }
        // A proposal already settled elsewhere is stale; take its card away.
        if (r.code === "ALREADY_RESOLVED" || r.code === "PROPOSAL_NOT_FOUND") removeProposal(p.id);
        setMessage(messageFor(r));
        return;
      }
      track({
        name: "rule_proposal_accepted",
        trigger_kind: p.trigger_kind,
        signal_count: p.signal_count,
        had_conflict: p.conflicts.length > 0,
      });
      track({
        name: "rules_edited",
        version: r.rules.version,
        author: "nob_proposal",
        chars: r.rules.markdown.length,
        sections_changed: sectionsChanged(rules.markdown, r.rules.markdown),
      });
      applied.current = stampOf(r.rules);
      setRules(r.rules);
      setDraft(r.rules.markdown);
      removeProposal(p.id);
      onRulesChanged(r.rules);
      say(`Saved as version ${r.rules.version}.`);
    },
    [rules.markdown, onRulesChanged, onSessionExpired, removeProposal, say],
  );

  const dismiss = useCallback(
    async (p: SurfacedProposal) => {
      setPropBusy((prev) => ({ ...prev, [p.id]: true }));
      const r = await dismissProposal(p.id);
      setPropBusy((prev) => ({ ...prev, [p.id]: false }));
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") {
          onSessionExpired();
          return;
        }
        // The dismissal did not land: the card stays, and nothing is reported
        // as done that was not.
        setMessage(messageFor(r));
        return;
      }
      track({
        name: "rule_proposal_dismissed",
        trigger_kind: p.trigger_kind,
        signal_count: p.signal_count,
        had_conflict: p.conflicts.length > 0,
      });
      // Fade first, then take it away — the acknowledgement is the point (FR-8.3).
      setDismissed((prev) => ({ ...prev, [p.id]: true }));
      const timer = setTimeout(() => removeProposal(p.id), NOTE_MS);
      fadeTimers.current.set(p.id, timer);
    },
    [onSessionExpired, removeProposal],
  );

  switch (view) {
    case "manual":
      return (
        <RulesEditor
          key="manual"
          initial={draft}
          baseline={rules.markdown}
          busy={busy}
          error={message?.text ?? null}
          errorLine={message?.line ?? null}
          onPreview={(markdown) => {
            setDraft(markdown);
            void propose({ markdown }, "manual");
          }}
          onCancel={home}
        />
      );

    case "diff":
      return proposal ? (
        <DiffView
          key="diff"
          lines={proposal.diff_lines}
          conflicts={proposal.conflicts}
          warnings={proposal.warnings}
          canKeepEditing={source === "manual"}
          currentVersion={rules.version}
          busy={busy}
          error={message?.text ?? null}
          errorLine={message?.line ?? null}
          onAccept={() => void accept()}
          onKeepEditing={() => {
            setDraft(proposal.resulting_markdown);
            setMessage(null);
            setView("manual");
          }}
          onDiscard={discard}
          onRestoreDefault={() => void restoreDefault()}
        />
      ) : null;

    case "history":
      return (
        <RulesHistory
          key="history"
          versions={versions}
          currentVersion={rules.version}
          loading={loadingVersions}
          error={message?.text ?? null}
          onOpen={(version) => void openVersion(version)}
          onBack={home}
        />
      );

    case "version":
      return viewing ? (
        <RulesVersionView
          key="version"
          rules={viewing}
          currentVersion={rules.version}
          busy={busy}
          error={message?.text ?? null}
          onRestore={() => void restore()}
          onBack={() => {
            setViewing(null);
            setMessage(null);
            setView("history");
          }}
        />
      ) : null;

    case "home":
      return (
        <RulesHome
          key="home"
          rules={rules}
          handle={handle}
          note={note}
          error={message?.text ?? null}
          proposals={
            <ProposalsSection
              proposals={proposals}
              current={rules.markdown}
              busy={propBusy}
              dismissed={dismissed}
              onAdd={(p) => void addProposal(p)}
              onAdjust={(p) => onAdjustProposal(p.id)}
              onDismiss={(p) => void dismiss(p)}
            />
          }
          onEditMyself={() => {
            setDraft(rules.markdown);
            setMessage(null);
            setView("manual");
          }}
          // "Tell Nob" IS the doorman chat now (owner decision 2026-08-05):
          // one conversation, one place, instead of a second instruction flow.
          onTellNob={onTellNob}
          onHistory={() => void openHistory()}
          onBack={onBack}
          backLabel={backLabel}
        />
      );
  }
}
